import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useEffect, lazy, Suspense } from "react";
import { PageLayout } from "@/components/layouts/PageLayout";

// Eager load critical pages for initial navigation
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import ProtectedRoute from "./components/ProtectedRoute";
import SellerProtectedRoute from "./components/SellerProtectedRoute";
import { AdminProtectedRoute } from "./components/AdminProtectedRoute";

// Lazy load pages that aren't needed on initial homepage load
const Category = lazy(() => import("./pages/Category"));
const Sales = lazy(() => import("./pages/Sales"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Cart = lazy(() => import("./pages/Cart"));
const SearchResults = lazy(() => import("./pages/SearchResults"));

// Lazy load secondary and authenticated pages
const ThankYou = lazy(() => import("./pages/ThankYou"));
const Auth = lazy(() => import("./pages/Auth"));
const BecomeSeller = lazy(() => import("./pages/BecomeSeller"));

// Lazy load seller onboarding pages
const SellerOnboardingStep1 = lazy(() => import("./pages/SellerOnboardingStep1"));
const SellerOnboardingStep2 = lazy(() => import("./pages/SellerOnboardingStep2"));
const SellerOnboardingStep3 = lazy(() => import("./pages/SellerOnboardingStep3"));
const SellerOnboardingStep4 = lazy(() => import("./pages/SellerOnboardingStep4"));
const SellerOnboardingStep5 = lazy(() => import("./pages/SellerOnboardingStep5"));
const SellerOnboardingStep6 = lazy(() => import("./pages/SellerOnboardingStep6"));
const Seller2FASetup = lazy(() => import("./pages/Seller2FASetup"));
const SellerSecuritySettings = lazy(() => import("./pages/SellerSecuritySettings"));

// Lazy load seller dashboard pages (includes recharts)
const SellerAddProduct = lazy(() => import("./pages/SellerAddProduct"));
const SellerEditProduct = lazy(() => import("./pages/SellerEditProduct"));
const SellerDashboard = lazy(() => import("./pages/SellerDashboard"));
const SellerProducts = lazy(() => import("./pages/SellerProducts"));
const SellerEarnings = lazy(() => import("./pages/SellerEarnings"));
const SellerShippingSettings = lazy(() => import("./pages/SellerShippingSettings"));
const SellerShopManager = lazy(() => import("./pages/SellerShopManager"));
const SellerOrders = lazy(() => import("./pages/SellerOrders"));
const SellerOrderDetail = lazy(() => import("./pages/SellerOrderDetail"));
const SellerMessages = lazy(() => import("./pages/SellerMessages"));

// Lazy load account pages
const Account = lazy(() => import("./pages/Account"));
const AccountDashboard = lazy(() => import("./pages/AccountDashboard"));
const AccountOrders = lazy(() => import("./pages/AccountOrders"));
const OrderDetail = lazy(() => import("./pages/OrderDetail"));
const AccountWishlist = lazy(() => import("./pages/AccountWishlist"));
const AccountAddresses = lazy(() => import("./pages/AccountAddresses"));
const AccountDetails = lazy(() => import("./pages/AccountDetails"));
const AccountMessages = lazy(() => import("./pages/AccountMessages"));

// Lazy load content pages
const MakerProfile = lazy(() => import("./pages/MakerProfile"));
const NewArrivals = lazy(() => import("./pages/NewArrivals"));
const Artisans = lazy(() => import("./pages/Artisans"));
const Gifts = lazy(() => import("./pages/Gifts"));
const Journal = lazy(() => import("./pages/Journal"));
const JournalPost = lazy(() => import("./pages/JournalPost"));
const TutorialArticle = lazy(() => import("./pages/TutorialArticle"));
const OurStory = lazy(() => import("./pages/OurStory"));
const Press = lazy(() => import("./pages/Press"));
const Contact = lazy(() => import("./pages/Contact"));
const SellerResources = lazy(() => import("./pages/SellerResources"));
const Community = lazy(() => import("./pages/Community"));

// Lazy load special pages
const GiftCards = lazy(() => import("./pages/GiftCards"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const SellerAgreement = lazy(() => import("./pages/SellerAgreement"));
import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { WishlistProvider } from "./contexts/WishlistContext";
import { UnderConstructionModal } from "./components/UnderConstructionModal";
import { WishlistAlerts } from "./components/WishlistAlerts";
import { CookieConsentBanner } from "./components/CookieConsentBanner";

const queryClient = new QueryClient();

// Component to handle automatic scroll to top on route change
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
const App = () => <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AuthProvider>
          <WishlistProvider>
            <CartProvider>
              <BrowserRouter>
              <WishlistAlerts />
              <UnderConstructionModal />
              <CookieConsentBanner />
              <ScrollToTop />

            <PageLayout className="py-0 md:py-[47px] my-0">
              <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}>
              <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/search-results" element={<SearchResults />} />
              <Route path="/category" element={<Category />} />
              <Route path="/category/:slug" element={<Category />} />
              <Route path="/sales" element={<Sales />} />
              {/* Category landing aliases */}
              <Route path="/category-pottery.html" element={<Navigate to="/category/pottery-and-ceramics" replace />} />
              <Route path="/category-textiles.html" element={<Navigate to="/category/textiles-and-weaving" replace />} />
              <Route path="/category-jewelry.html" element={<Navigate to="/category/jewelry-and-accessories" replace />} />
              <Route path="/category-woodcraft.html" element={<Navigate to="/category/woodcraft" replace />} />
              <Route path="/category-food.html" element={<Navigate to="/category/food-and-olive-oil" replace />} />
              <Route path="/category-lace.html" element={<Navigate to="/category/traditional-lace" replace />} />
              <Route path="/category-3dprints.html" element={<Navigate to="/category/3d-prints" replace />} />

              <Route path="/product/:id" element={<ProductDetail />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/thank-you" element={<ThankYou />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/become-a-seller" element={<BecomeSeller />} />
              <Route path="/seller-onboarding-step1" element={<ProtectedRoute><SellerOnboardingStep1 /></ProtectedRoute>} />
              <Route path="/seller-onboarding-step2" element={<ProtectedRoute><SellerOnboardingStep2 /></ProtectedRoute>} />
              <Route path="/seller-onboarding-step3" element={<ProtectedRoute><SellerOnboardingStep3 /></ProtectedRoute>} />
              <Route path="/seller-onboarding-add-product" element={<ProtectedRoute><SellerAddProduct /></ProtectedRoute>} />
              <Route path="/seller-onboarding-step4" element={<ProtectedRoute><SellerOnboardingStep4 /></ProtectedRoute>} />
              <Route path="/seller-onboarding-step5" element={<ProtectedRoute><SellerOnboardingStep5 /></ProtectedRoute>} />
              <Route path="/seller-onboarding-step6" element={<ProtectedRoute><SellerOnboardingStep6 /></ProtectedRoute>} />
              <Route path="/seller-2fa-setup" element={<ProtectedRoute><Seller2FASetup /></ProtectedRoute>} />
            <Route path="/seller-add-product" element={<SellerProtectedRoute><SellerAddProduct /></SellerProtectedRoute>} />
            <Route path="/seller-edit-product/:id" element={<SellerProtectedRoute><SellerEditProduct /></SellerProtectedRoute>} />
            <Route path="/seller-dashboard" element={<SellerProtectedRoute><SellerDashboard /></SellerProtectedRoute>} />
            <Route path="/seller-dashboard/orders" element={<SellerProtectedRoute><SellerOrders /></SellerProtectedRoute>} />
            <Route path="/seller-dashboard/orders/:orderId" element={<SellerProtectedRoute><SellerOrderDetail /></SellerProtectedRoute>} />
            <Route path="/seller-dashboard/messages" element={<SellerProtectedRoute><SellerMessages /></SellerProtectedRoute>} />
            <Route path="/seller-products" element={<SellerProtectedRoute><SellerProducts /></SellerProtectedRoute>} />
            <Route path="/seller-earnings" element={<SellerProtectedRoute><SellerEarnings /></SellerProtectedRoute>} />
            <Route path="/seller-shipping-settings" element={<SellerProtectedRoute><SellerShippingSettings /></SellerProtectedRoute>} />
              <Route path="/seller-shop-manager" element={<SellerProtectedRoute><SellerShopManager /></SellerProtectedRoute>} />
              <Route path="/seller-security" element={<SellerProtectedRoute><SellerSecuritySettings /></SellerProtectedRoute>} />
              <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>}>
                <Route index element={<AccountDashboard />} />
                <Route path="orders" element={<AccountOrders />} />
                <Route path="orders/:id" element={<OrderDetail />} />
                <Route path="wishlist" element={<AccountWishlist />} />
                <Route path="messages" element={<AccountMessages />} />
                <Route path="addresses" element={<AccountAddresses />} />
                <Route path="details" element={<AccountDetails />} />
              </Route>
              <Route path="/maker/:id" element={<MakerProfile />} />
              <Route path="/new-arrivals" element={<NewArrivals />} />
              <Route path="/artisans" element={<Artisans />} />
              <Route path="/gifts" element={<Gifts />} />
              <Route path="/journal" element={<Journal />} />
              <Route path="/journal/:slug" element={<JournalPost />} />
              
              <Route path="/tutorial/:slug" element={<TutorialArticle />} />
              <Route path="/our-story" element={<OurStory />} />
              <Route path="/press" element={<Press />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/seller-resources" element={<SellerResources />} />
              <Route path="/community" element={<Community />} />
              
              <Route path="/gift-cards" element={<GiftCards />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/seller-agreement" element={<SellerAgreement />} />


              {/* Admin routes */}
              <Route path="/admin" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
              <Route path="/admin-dashboard.html" element={<Navigate to="/admin" replace />} />

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
            </PageLayout>
            </BrowserRouter>
            </CartProvider>
          </WishlistProvider>
        </AuthProvider>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>;
export default App;